import { createContext, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetcher } from 'services/helpers/responseHandler'

import { loginUser, qrLoggingIn, setSession } from '../../../actions/index'
import { gotSocketMessage, wsConnected, wsDisconnected } from '../../../actions/socket'
import config from '../../../services/config'
import LocalUserService from '../../../services/localUserService'
import { clearRequestInterval, requestInterval } from '../../../utils/requestInterval'

const userService = new LocalUserService()

const types = {
  phoneLogin: 200,
  codeLogin: 201,
  confirmLogin: 'DashboardConfirmLogin',
  logout: 220
}

const nonJSONresponses = [
  'not understood',
  'invalid json',
  'invalid request',
  'pong',
  'Invalid session'
]

export const SocketContext = createContext()

const log = async ({ level = 'Info', message = '', additionalData = {} }) => {
  try {
    fetcher.post('logs', {
      level,
      message,
      additionalData: {
        ...additionalData
      }
    })
  } catch (error) {
    console.warn('failed to post log', error)
  }
}

let ws

export function connectLoginSocket(dispatch, sessionId) {
  if (ws && ws.readyState === WebSocket.OPEN) {
    return
  }

  ws = new WebSocket(`${config.socketUrl}?sessionId=${sessionId}`)
  ws.onopen = function () {
    log({
      level: 'Debug',
      message: 'Connected Login Socket',
      additionalData: { socketUrl: `${config.socketUrl}?sessionId=${sessionId}` }
    })
    dispatch(wsConnected())
  }

  ws.onclose = function () {
    log({
      level: 'Debug',
      message: 'Closing login socket',
      additionalData: { socketUrl: `${config.socketUrl}?sessionId=${sessionId}` }
    })
    dispatch(wsDisconnected())
  }

  ws.onerror = function (err) {
    log({
      level: 'Warn',
      message: `Socket encountered error: ${err.message} Closing login socket`,
      additionalData: { socketUrl: `${config.socketUrl}?sessionId=${sessionId}` }
    })
    console.error('Socket encountered error: ', err.message, 'Closing socket')
    ws.close()
  }
}

export function disconnectLoginSocket() {
  log({
    level: 'Debug',
    message: 'Disconnected Login Socket'
  })
  ws.close()
}

const SocketProvider = ({ children }) => {
  const dispatch = useDispatch()
  const { connected } = useSelector(s => s.socket)
  const { userLoggedIn } = useSelector(s => s.login)

  let pingInterval = useRef()

  function setPingInterval() {
    if (pingInterval.current) {
      clearRequestInterval(pingInterval.current)
    }

    pingInterval.current = requestInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send('ping')
      } else {
        clearRequestInterval(pingInterval.current)
      }
    }, 4000)
  }

  function connectSocket(sessionId) {
    if (!connected && (!ws || (ws && ws.readyState === 3))) {
      connectLoginSocket(dispatch, sessionId, log)
    }
  }

  useEffect(() => {
    if (connected && ws) {
      setPingInterval()
    }
  }, [connected])

  useEffect(() => {
    if (userLoggedIn) {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close()
        clearRequestInterval(pingInterval.current)
      }
    }
  }, [userLoggedIn])

  useEffect(() => {
    if (connected) {
      ws.onmessage = e => {
        if (nonJSONresponses.includes(e.data)) return

        const res = JSON.parse(e.data)

        if (res.type === types.confirmLogin) {
          const { data } = res

          if (data.isSuccessfull) {
            userService.setCurrentUserInfo(data)
            userService.storeLastUser(data.user)

            dispatch(qrLoggingIn())
            dispatch(setSession(data.sessionId))
            dispatch(loginUser(data.user, data.userToken, data.userId, data.sessionId))
          }
        } else {
          dispatch(gotSocketMessage(res))
        }
      }
    }
  }, [connected, dispatch])

  return (
    <SocketContext.Provider value={[ws, setPingInterval, connectSocket]}>
      {children}
    </SocketContext.Provider>
  )
}

export default SocketProvider
